import { useState } from 'react';
import { Section } from '@elfsight/lib-stripe-integration';
import { Show } from '@elfsight/lib-utils';
import { useFormDispatch } from '@elfsight/widget-form-builder/widget';
import { Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import type { PaymentPageProps } from '../../../common';
import { BackButton, Footer } from '../blocks';
import {
  useCheckoutColors,
  useCheckoutI18n,
  useCheckoutNavigation
} from '../providers';
import { useCustomerEmail } from '../utils';
import { PaymentConfirmationEmail } from './payment-confirmation-email';
import { StripePaymentForm } from './payment-form';

const COMPONENT_NAME = 'payment-form-page';

export function PaymentPage({
  emailFieldId,
  productData,
  paymentMetadata,
  onPaymentSuccess,
  footerText
}: PaymentPageProps) {
  const { t } = useCheckoutI18n();
  const { withForm, setCheckoutStep } = useCheckoutNavigation();
  const { textColor, accentColor } = useCheckoutColors();
  const dispatch = useFormDispatch();

  const { field: emailField, initialValue: initialEmail } =
    useCustomerEmail(emailFieldId);

  const [customerEmail, setCustomerEmail] = useState(initialEmail);

  const handleBack = () => {
    dispatch({
      type: 'changeField',
      field: { id: emailField.id, valid: true }
    });
    setCheckoutStep('form');
  };

  return (
    <Section
      className={createReadableClass({ component: COMPONENT_NAME })}
      $gap={20}
      $align="flex-start"
      $withoutTopPadding
      $withoutBottomPadding
      $stretch
    >
      <Section
        className={createReadableClass({
          component: COMPONENT_NAME,
          element: 'heading'
        })}
        $gap={8}
        $align="flex-start"
        $withoutTopPadding
        $withoutBottomPadding
      >
        <Show when={withForm}>
          <BackButton color={accentColor} onClick={handleBack} />
        </Show>

        <Typography color={textColor} textToken="text24Bold">
          {t('Complete Your Payment')}
        </Typography>
      </Section>

      <PaymentConfirmationEmail
        emailField={emailField}
        initialValue={initialEmail}
        onEmailChange={setCustomerEmail}
      />

      <Show when={!!customerEmail}>
        <StripePaymentForm
          productData={productData}
          customerEmail={customerEmail}
          onPaymentSuccess={onPaymentSuccess}
          paymentMetadata={paymentMetadata}
        />
      </Show>

      <Show when={!!footerText}>
        <Footer textColor={textColor} text={footerText!} withoutTopPadding />
      </Show>
    </Section>
  );
}
